import { Cartesian3 } from "cesium";
/**
 * Computes the closest point on a line segment to a given point.
 *
 * @param point - The point to project onto the line segment
 * @param start - The start point of the line segment
 * @param end - The end point of the line segment
 * @param clampToLine - If true, clamps the result to the segment endpoints when the projection falls outside [start, end]
 * @returns The closest point on the line segment (or line if clampToLine is false)
 */
export function closestPointOnLine(point, start, end, clampToLine) {
    const startPoint = Cartesian3.subtract(point, start, new Cartesian3());
    const startEnd = Cartesian3.subtract(end, start, new Cartesian3());
    const startEnd2 = Cartesian3.dot(startEnd, startEnd);
    if (startEnd2 === 0) {
        return Cartesian3.clone(start, new Cartesian3());
    }
    const startEnd_startPoint = Cartesian3.dot(startEnd, startPoint);
    let t = startEnd_startPoint / startEnd2;
    if (clampToLine) {
        t = Math.max(0, Math.min(1, t));
    }
    const result = Cartesian3.multiplyByScalar(startEnd, t, new Cartesian3());
    Cartesian3.add(result, start, result);
    return result;
}
/**
 * Computes the perpendicular distance from a point to a line segment.
 *
 * @param point - The point to measure the distance from
 * @param start - The start point of the line segment
 * @param end - The end point of the line segment
 * @param clampToLine - If true, clamps the result to the segment endpoints when the projection falls outside [start, end]
 * @returns The perpendicular distance from the point to the line segment
 */
export function perpendicularDistanceFromPointToLine(point, start, end, clampToLine) {
    const closestPoint = closestPointOnLine(point, start, end, clampToLine);
    return Cartesian3.distance(point, closestPoint);
}
/**
 * Calculate the angle (in degrees) between two direction vectors
 * @param dir1 First direction vector
 * @param dir2 Second direction vector
 * @param defaultAngle The default angle to return for invalid vectors (default: 180)
 * @returns Angle in degrees (0-180), or defaultAngle if vectors are invalid
 */
export function calculateAngleBetweenVectors(dir1, dir2, defaultAngle = 180) {
    // Validate direction vectors (check for zero vectors)
    const mag1 = Cartesian3.magnitude(dir1);
    const mag2 = Cartesian3.magnitude(dir2);
    if (mag1 < 1e-10 || mag2 < 1e-10) {
        // Return default angle for invalid vectors
        return defaultAngle;
    }
    // Use Cesium's built-in angleBetween (returns radians)
    const angleRad = Cartesian3.angleBetween(dir1, dir2);
    return angleRad * (180 / Math.PI);
}
//# sourceMappingURL=math-utils.js.map